import { createContext, useContext } from 'react';
import { makeAutoObservable, runInAction } from 'mobx';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import InitiateQuoteFlowDelegate from 'store/flows/initiate-quote/initiateQuoteFlowDelegate';
import { RouteV2Id } from 'routes/routesConfigV2';
import generateDefaultProposalName from 'global/utils/generateDefaultProposalName';
import { AlliedInitiateProposalFormDelegate } from './proposalFormDelegate';

interface DomainParams {
  groupId: string;
}

export default class AlliedInitiateProposalPageStore {
  private _isActionLoading = false;
  private _rateSegment: RateSegment = 'GROUP';
  public initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate;
  public domainParams: DomainParams;
  public proposalFormDelegate: AlliedInitiateProposalFormDelegate;

  public get proposalForm(): AlliedInitiateProposalFormDelegate['proposalForm'] {
    return this.proposalFormDelegate.proposalForm;
  }

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  public get canCompleteStep(): boolean {
    return (
      this.proposalForm.basicFieldsValidationsPassed &&
      !this.proposalForm.isLoading &&
      !this._isActionLoading &&
      !this.initiateQuoteFlowDelegate.isInitialDataLoading
    );
  }

  private _setActionLoading(isLoading: boolean): void {
    this._isActionLoading = isLoading;
  }

  public async getInitialData(): Promise<void> {
    this.initiateQuoteFlowDelegate.setIsInitialDataLoading(true);

    await this.initiateQuoteFlowDelegate.getFlowInitialData();
    runInAction(() => {
      this.proposalFormDelegate.proposalForm.populateFields({
        proposalName: generateDefaultProposalName(this.initiateQuoteFlowDelegate.groupModule.groupName),
      });
      this.initiateQuoteFlowDelegate.setIsInitialDataLoading(false);
    });
  }

  public async saveProposal(): Promise<void> {
    this._setActionLoading(true);
    const { effectiveDate, zip, state, county, proposalName, sic4 } = this.proposalForm.getFormDataObject() as Record<
      string,
      string
    >;

    await this.initiateQuoteFlowDelegate.createCartAndSaveProposal({
      effectiveDate,
      proposalName,
      rateSegment: this._rateSegment,
      browsingCriteria: {
        effectiveDate,
        zipCode: zip,
        state,
        county,
        sic4,
        shortName: {},
      },
    });

    runInAction(() => {
      this._setActionLoading(false);
      this.initiateQuoteFlowDelegate.onCompleteRedirect();
    });
  }

  constructor(params: DomainParams) {
    this.domainParams = params;

    this.initiateQuoteFlowDelegate = new InitiateQuoteFlowDelegate({
      domainParams: { groupId: params.groupId, nextPageId: RouteV2Id.QUOTE_CHECKLIST },
    });
    this.proposalFormDelegate = new AlliedInitiateProposalFormDelegate({
      rateSegment: this._rateSegment,
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const AlliedInitiateProposalPageStoreContext = createContext(
  new AlliedInitiateProposalPageStore({
    groupId: '',
  }),
);

export const useAlliedInitiateProposalPageStore = (): AlliedInitiateProposalPageStore => {
  return useContext(AlliedInitiateProposalPageStoreContext);
};

export { AlliedInitiateProposalPageStoreContext };
